import { H2, Img, Link, P, Skeleton, Space } from '@dnb/eufemia';
import type { SpacingElementProps } from '@dnb/eufemia/shared/types';
import classNames from 'classnames';
import { Link as RouterLink } from 'react-router-dom';

import type { SeverityStatus } from '@/utils/apiStatusSeverity';
import { SEVERITY_CLASS_SUFFIX } from '@/utils/apiStatusSeverity';

import style from './index.module.css';

import downImageUrl from './Down.svg?url';
import operationalImageUrl from './Operational.svg?url';
import unstableImageUrl from './Unstable.svg?url';

interface StatusWidgetProps extends SpacingElementProps {
  severity: SeverityStatus;
  headings?: Record<SeverityStatus, string>;
  content?: Record<SeverityStatus, React.ReactNode>;
  skeleton?: boolean;
}

const HEADINGS: Record<SeverityStatus, string> = {
  OK: 'All APIs are operational',
  INSTABILITIES: 'Some APIs may have instabilities',
  REDUCED_SERVICE: 'Some APIs have reduced service',
};

const StatusPageLink = (
  <>
    Please visit the{' '}
    <Link element={RouterLink} to="/status">
      status page
    </Link>{' '}
    for updates.
  </>
);

const LookingIntoIt = (
  <>
    We are looking into it. Please visit the{' '}
    <Link element={RouterLink} to="/status">
      status page
    </Link>{' '}
    for updates.
  </>
);

const CONTENT: Record<SeverityStatus, React.ReactNode> = {
  OK: StatusPageLink,
  INSTABILITIES: LookingIntoIt,
  REDUCED_SERVICE: LookingIntoIt,
};

const ILLUSTRATION: Record<SeverityStatus, string> = {
  OK: operationalImageUrl,
  INSTABILITIES: unstableImageUrl,
  REDUCED_SERVICE: downImageUrl,
};

export default function StatusWidget({
  severity,
  headings = HEADINGS,
  content = CONTENT,
  skeleton,
  ...props
}: StatusWidgetProps) {
  return (
    <Skeleton show={skeleton}>
      <Space
        className={classNames([
          style['StatusWidget'],
          style[`StatusWidget--severity-${SEVERITY_CLASS_SUFFIX[severity]}`],
          'dnb-drop-shadow',
        ])}
        {...props}
      >
        <Img alt={headings[severity]} src={ILLUSTRATION[severity]} width={160} />
        <div className={style['StatusWidget-text']}>
          <H2 className={style['StatusWidget-header']} size="medium">
            {headings[severity]}
          </H2>
          <P top="small">{content[severity]}</P>
        </div>
      </Space>
    </Skeleton>
  );
}
